import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Receipt,
  ShoppingCart,
  Truck,
  MoreHorizontal,
  Calendar,
  CheckCircle,
  Clock,
  Building2,
} from "lucide-react";

const CATEGORIA_ICONS = {
  insumos: ShoppingCart,
  transporte: Truck,
  otros: MoreHorizontal,
};

const CATEGORIA_COLORS = {
  insumos: "#8B4513",
  transporte: "#3B82F6",
  otros: "#6B7280",
};

export default function VendedorGastos() {
  const { user } = useAuth();
  const [gastos, setGastos] = useState([]);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    descripcion: "",
    monto: "",
    categoria: "insumos",
    fecha_gasto: new Date().toISOString().split("T")[0],
    // Proveedor
    proveedor_razon_social: "",
    proveedor_tipo_documento: "nit",
    proveedor_numero_documento: "",
    proveedor_telefono: "",
    // Documento
    documento_tipo: "factura_electronica",
    documento_numero: "",
    documento_fecha: new Date().toISOString().split("T")[0],
    // Pago
    estado_pago: "pagado",
    metodo_pago: "efectivo",
    // Notas
    notas: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gastosRes, enumsRes] = await Promise.all([
        api.get("/gastos/vendedor/mis-gastos"),
        api.get("/gastos/vendedor/enums"),
      ]);
      setGastos(gastosRes.data.gastos || []);
      setEnums(enumsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("No tienes permiso para registrar gastos");
      } else {
        toast.error("Error al cargar los datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.descripcion || !form.monto || !form.proveedor_razon_social || !form.documento_numero) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        descripcion: form.descripcion,
        monto: parseFloat(form.monto),
        categoria: form.categoria,
        fecha_gasto: form.fecha_gasto,
        proveedor: {
          razon_social: form.proveedor_razon_social,
          tipo_documento: form.proveedor_tipo_documento,
          numero_documento: form.proveedor_numero_documento,
          telefono: form.proveedor_telefono || null,
        },
        documento: {
          tipo: form.documento_tipo,
          numero: form.documento_numero,
          fecha_documento: form.documento_fecha,
        },
        estado_pago: form.estado_pago,
        metodo_pago: form.metodo_pago || null,
        notas: form.notas || null,
      };

      const res = await api.post("/gastos/vendedor", payload);
      toast.success(`Gasto registrado - Consecutivo #${res.data.consecutivo}`);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      descripcion: "",
      monto: "",
      categoria: "insumos",
      fecha_gasto: new Date().toISOString().split("T")[0],
      proveedor_razon_social: "",
      proveedor_tipo_documento: "nit",
      proveedor_numero_documento: "",
      proveedor_telefono: "",
      documento_tipo: "factura_electronica",
      documento_numero: "",
      documento_fecha: new Date().toISOString().split("T")[0],
      estado_pago: "pagado",
      metodo_pago: "efectivo",
      notas: "",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate totals
  const totalGastos = gastos.reduce((sum, g) => sum + (g.monto || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-gastos">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Registro de Gastos</h1>
          <p className="text-torta-muted">Registra compras de insumos y materia prima</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-torta-brown hover:bg-torta-brown/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Gasto
        </Button>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Receipt className="w-7 h-7" />
          </div>
          <div>
            <p className="text-white/80 text-sm">Mis Gastos Registrados</p>
            <p className="font-display text-3xl">{formatCurrency(totalGastos)}</p>
            <p className="text-white/70 text-sm">{gastos.length} registros</p>
          </div>
        </div>
      </div>

      {/* Gastos List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-torta-sand">
          <h2 className="font-display text-lg text-torta-dark">Historial de Gastos</h2>
        </div>

        {gastos.length === 0 ? (
          <div className="p-8 text-center text-torta-muted">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No has registrado gastos aún</p>
          </div>
        ) : (
          <div className="divide-y divide-torta-sand">
            {gastos.map((gasto) => {
              const CatIcon = CATEGORIA_ICONS[gasto.categoria] || MoreHorizontal;
              const catColor = CATEGORIA_COLORS[gasto.categoria] || "#6B7280";
              
              return (
                <div key={gasto.id} className="p-4 hover:bg-torta-cream/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${catColor}20` }}
                    >
                      <CatIcon className="w-5 h-5" style={{ color: catColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-torta-dark truncate">{gasto.descripcion}</p>
                      <div className="flex items-center gap-3 text-sm text-torta-muted">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(gasto.fecha_gasto)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {gasto.proveedor?.razon_social || "Sin proveedor"}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg text-torta-dark">
                        {formatCurrency(gasto.monto)}
                      </p>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          gasto.estado_pago === "pagado"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {gasto.estado_pago === "pagado" ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        {gasto.estado_pago === "pagado" ? "Pagado" : "Pendiente"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog for new expense */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Registrar Gasto</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-4 p-4 bg-torta-cream/50 rounded-xl">
              <h3 className="font-medium text-torta-dark text-sm flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Información del Gasto
              </h3>

              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Ej: Compra de harina para producción"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    value={form.monto}
                    onChange={(e) => setForm({ ...form, monto: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select
                    value={form.categoria}
                    onValueChange={(value) => setForm({ ...form, categoria: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.categorias?.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fecha del Gasto</Label>
                <Input
                  type="date"
                  value={form.fecha_gasto}
                  onChange={(e) => setForm({ ...form, fecha_gasto: e.target.value })}
                />
              </div>
            </div>

            {/* Proveedor Info */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
              <h3 className="font-medium text-blue-800 text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Datos del Proveedor
              </h3>

              <div className="space-y-2">
                <Label>Nombre o Razón Social *</Label>
                <Input
                  value={form.proveedor_razon_social}
                  onChange={(e) => setForm({ ...form, proveedor_razon_social: e.target.value })}
                  placeholder="Nombre del proveedor"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo Doc</Label>
                  <Select
                    value={form.proveedor_tipo_documento}
                    onValueChange={(value) => setForm({ ...form, proveedor_tipo_documento: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.tipos_documento_proveedor?.map((tipo) => (
                        <SelectItem key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input
                    value={form.proveedor_numero_documento}
                    onChange={(e) => setForm({ ...form, proveedor_numero_documento: e.target.value })}
                    placeholder="NIT o CC"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Teléfono (opcional)</Label>
                <Input
                  value={form.proveedor_telefono}
                  onChange={(e) => setForm({ ...form, proveedor_telefono: e.target.value })}
                  placeholder="Teléfono del proveedor"
                />
              </div>
            </div>

            {/* Document Info */}
            <div className="space-y-4 p-4 bg-green-50 rounded-xl">
              <h3 className="font-medium text-green-800 text-sm flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Documento Soporte
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo Documento</Label>
                  <Select
                    value={form.documento_tipo}
                    onValueChange={(value) => setForm({ ...form, documento_tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {enums?.tipos_documento_soporte?.map((tipo) => (
                        <SelectItem key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Número *</Label>
                  <Input
                    value={form.documento_numero}
                    onChange={(e) => setForm({ ...form, documento_numero: e.target.value })}
                    placeholder="# Factura"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fecha del Documento</Label>
                <Input
                  type="date"
                  value={form.documento_fecha}
                  onChange={(e) => setForm({ ...form, documento_fecha: e.target.value })}
                />
              </div>
            </div>

            {/* Payment Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado de Pago</Label>
                <Select
                  value={form.estado_pago}
                  onValueChange={(value) => setForm({ ...form, estado_pago: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enums?.estados_pago?.map((estado) => (
                      <SelectItem key={estado.id} value={estado.id}>
                        {estado.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <Select
                  value={form.metodo_pago}
                  onValueChange={(value) => setForm({ ...form, metodo_pago: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enums?.metodos_pago?.map((metodo) => (
                      <SelectItem key={metodo.id} value={metodo.id}>
                        {metodo.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-torta-brown hover:bg-torta-brown/90"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </span>
                ) : (
                  "Registrar Gasto"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
